import type { RolePublic } from '@api/endpoints/auth/get-auth-self-schema'
import { UserRole } from '@api/types'
import type { TFunction } from 'i18next'

export const roleHierarchy: Record<string, number> = {
  ADMIN: 3,
  MANAGER: 2,
  USER: 1,
}

/**
 * Check if the user has manager level access or above for a specific workspace
 * @param roles Array of user roles across workspaces
 * @param workspaceId The workspace ID to check permissions for
 * @returns True if user is manager or above in the specified workspace
 */
export function isManagerOrAbove(
  roles: RolePublic[],
  workspaceId: string
): boolean {
  const userRole = roles.find((role) => role.workspaceId === workspaceId)
  if (!userRole) return false

  const roleLevel = roleHierarchy[userRole.role] ?? 0
  const managerLevel = roleHierarchy.MANAGER ?? 0

  return roleLevel >= managerLevel
}

export function isAdmin(roles: RolePublic[]): boolean {
  return roles.some((role) => role.role === UserRole.ADMIN)
}

export function isWorkspaceAdmin(
  roles: RolePublic[],
  workspaceId: string
): boolean {
  return (
    roles.find((role) => role.workspaceId === workspaceId)?.role ===
    UserRole.ADMIN
  )
}

export const getRoleLabel = (t: TFunction, role?: string): string | null => {
  switch (role) {
    case UserRole.ADMIN:
      return t('core.roles.admin')
    case UserRole.MANAGER:
      return t('core.roles.manager')
    case UserRole.USER:
      return t('core.roles.member')
    default:
      return null
  }
}
